// constants/typography.ts
import type { 
  FontFamily, 
  FontWeight, 
  FontSize, 
  TextAlign, 
  LineHeight, 
  LetterSpacing,
  TypographyConfig 
} from '../types/typography';

export const FONT_FAMILIES: Record<string, FontFamily> = {
  SATOSHI: 'satoshi',
  MONTSERRAT: 'montserrat',
  INTER: 'inter',
  POPPINS: 'poppins',
  HELVETICA: 'helvetica'
} as const;

export const FONT_WEIGHTS: Record<string, FontWeight> = {
  THIN: 'thin',           // 100
  EXTRA_LIGHT: 'extraLight', // 200
  LIGHT: 'light',         // 300
  REGULAR: 'regular',     // 400
  MEDIUM: 'medium',       // 500
  SEMI_BOLD: 'semiBold',  // 600
  BOLD: 'bold',          // 700
  EXTRA_BOLD: 'extraBold', // 800
  BLACK: 'black'         // 900
} as const;

export const FONT_SIZES: Record<string, FontSize> = {
  XS: 'xs',       // 12px
  SM: 'sm',       // 14px
  BASE: 'base',   // 16px
  LG: 'lg',       // 18px
  XL: 'xl',       // 20px
  XL2: 'xl2',     // 24px
  XL3: 'xl3',     // 30px
  XL4: 'xl4',     // 36px
  XL5: 'xl5',     // 48px
  XL6: 'xl6',     // 60px
  XL7: 'xl7'      // 72px
} as const;

export const TEXT_ALIGN: Record<string, TextAlign> = {
  LEFT: 'left',
  CENTER: 'center',
  RIGHT: 'right',
  JUSTIFY: 'justify'
} as const;

export const LINE_HEIGHT: Record<string, LineHeight> = {
  TIGHT: 'leadingTight',
  SNUG: 'leadingSnug',
  NORMAL: 'leadingNormal',
  RELAXED: 'leadingRelaxed',
  LOOSE: 'leadingLoose'
} as const;

export const LETTER_SPACING: Record<string, LetterSpacing> = {
  TIGHTER: 'trackingTighter',
  TIGHT: 'trackingTight',
  NORMAL: 'trackingNormal',
  WIDE: 'trackingWide',
  WIDER: 'trackingWider',
  WIDEST: 'trackingWidest'
} as const;

// Typography presets matching your Figma design
export const TYPOGRAPHY_PRESETS: Record<string, TypographyConfig> = {
  HEADING_1: {
    fontFamily: FONT_FAMILIES.SATOSHI,
    weight: FONT_WEIGHTS.BOLD,
    size: FONT_SIZES.XL5,
    lineHeight: LINE_HEIGHT.TIGHT,
    letterSpacing: LETTER_SPACING.TIGHT
  },
  HEADING_2: {
    fontFamily: FONT_FAMILIES.SATOSHI,
    weight: FONT_WEIGHTS.SEMI_BOLD,
    size: FONT_SIZES.XL4,
    lineHeight: LINE_HEIGHT.TIGHT,
    letterSpacing: LETTER_SPACING.TIGHT
  },
  HEADING_3: {
    fontFamily: FONT_FAMILIES.SATOSHI,
    weight: FONT_WEIGHTS.SEMI_BOLD,
    size: FONT_SIZES.XL3,
    lineHeight: LINE_HEIGHT.SNUG
  },
  HEADING_4: {
    fontFamily: FONT_FAMILIES.SATOSHI,
    weight: FONT_WEIGHTS.MEDIUM,
    size: FONT_SIZES.XL2,
    lineHeight: LINE_HEIGHT.SNUG
  },
  HEADING_5: {
    fontFamily: FONT_FAMILIES.SATOSHI,
    weight: FONT_WEIGHTS.MEDIUM,
    size: FONT_SIZES.XL,
    lineHeight: LINE_HEIGHT.NORMAL
  },
  HEADING_6: {
    fontFamily: FONT_FAMILIES.SATOSHI,
    weight: FONT_WEIGHTS.MEDIUM,
    size: FONT_SIZES.LG,
    lineHeight: LINE_HEIGHT.NORMAL
  },
  BODY_LARGE: {
    fontFamily: FONT_FAMILIES.SATOSHI,
    weight: FONT_WEIGHTS.REGULAR,
    size: FONT_SIZES.LG,
    lineHeight: LINE_HEIGHT.RELAXED
  },
  BODY_REGULAR: {
    fontFamily: FONT_FAMILIES.SATOSHI,
    weight: FONT_WEIGHTS.REGULAR,
    size: FONT_SIZES.BASE,
    lineHeight: LINE_HEIGHT.NORMAL
  },
  BODY_SMALL: {
    fontFamily: FONT_FAMILIES.SATOSHI,
    weight: FONT_WEIGHTS.REGULAR,
    size: FONT_SIZES.SM,
    lineHeight: LINE_HEIGHT.NORMAL
  },
  BUTTON: {
    fontFamily: FONT_FAMILIES.SATOSHI,
    weight: FONT_WEIGHTS.MEDIUM,
    size: FONT_SIZES.SM,
    lineHeight: LINE_HEIGHT.TIGHT,
    letterSpacing: LETTER_SPACING.WIDE
  },
  CAPTION: {
    fontFamily: FONT_FAMILIES.SATOSHI,
    weight: FONT_WEIGHTS.REGULAR,
    size: FONT_SIZES.XS,
    lineHeight: LINE_HEIGHT.SNUG
  }
} as const;

// Font weight mapping for type safety
export const FONT_WEIGHT_VALUES: Record<FontWeight, number> = {
  thin: 100,
  extraLight: 200,
  light: 300,
  regular: 400,
  medium: 500,
  semiBold: 600,
  bold: 700,
  extraBold: 800,
  black: 900
} as const;

// Available weights per font family
export const AVAILABLE_WEIGHTS: Record<FontFamily, FontWeight[]> = {
  satoshi: ['thin', 'extraLight', 'light', 'regular', 'medium', 'semiBold', 'bold', 'extraBold', 'black'],
  montserrat: ['regular', 'medium', 'semiBold', 'bold'],
  inter: ['regular'],
  poppins: ['semiBold'],
  helvetica: ['regular']
};